import { Link } from 'react-router-dom';

const oddsFormats = [
  {
    name: 'Decimales (Europa)',
    example: '2.50',
    formula: 'Ganancia = Stake × Cuota',
    calc: '100€ × 2.50 = 250€ (150€ de beneficio)',
    desc: 'El formato más usado en Europa. La cuota incluye el stake. Fácil de comparar y calcular.',
    color: '#00e87a',
  },
  {
    name: 'Fraccionales (UK)',
    example: '3/2',
    formula: 'Ganancia = Stake × (Num/Den)',
    calc: '100€ × (3/2) = 150€ de beneficio',
    desc: 'Tradicional en Reino Unido. El numerador es el beneficio por cada unidad del denominador apostada.',
    color: '#06b6d4',
  },
  {
    name: 'Americanas (Moneyline)',
    example: '+150 / -200',
    formula: '+150: ganas 150 por 100 apostados\n-200: apuestas 200 para ganar 100',
    calc: '+150 → cuota decimal 2.50\n-200 → cuota decimal 1.50',
    desc: 'Estándar en EE.UU. Los positivos indican el beneficio por 100 apostados; los negativos, lo que hay que apostar para ganar 100.',
    color: '#f59e0b',
  },
];

const marginTips = [
  { icon: 'ri-store-2-line', title: 'Margen de la casa', desc: 'Las casas añaden un margen (overround) a las cuotas. En 1X2 de fútbol suele ser 5-8%. En hándicap asiático baja al 2-3%. Busca mercados con menor margen.' },
  { icon: 'ri-arrow-left-right-line', title: 'Compara siempre', desc: 'Una diferencia de 0.10 en la cuota puede suponer un 5% más de retorno. Con el tiempo, apostar en la mejor cuota disponible marca una diferencia enorme en el ROI.' },
  { icon: 'ri-time-line', title: 'Timing de las cuotas', desc: 'Las cuotas de apertura suelen tener más valor. El mercado se vuelve más eficiente conforme se acerca el partido. Actúa rápido cuando encuentres valor.' },
  { icon: 'ri-line-chart-line', title: 'Movimiento de cuotas', desc: 'Una cuota que baja mucho indica dinero profesional en ese lado. Una que sube puede indicar noticias negativas (lesiones, alineaciones). Interpreta los movimientos.' },
];

export default function OddsSection() {
  return (
    <section id="cuotas" className="py-20 bg-[#0a0c11]">
      <div className="max-w-[1440px] mx-auto px-6">
        {/* Header */}
        <div className="flex items-start gap-4 mb-12">
          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#06b6d4]/10 border border-[#06b6d4]/20 flex-shrink-0">
            <i className="ri-scales-3-line text-[#06b6d4] text-xl"></i>
          </div>
          <div>
            <p className="text-[#06b6d4] text-xs font-semibold uppercase tracking-widest mb-1">Capítulo 4</p>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>
              Entender las Cuotas
            </h2>
            <p className="text-[#a8b2c1] text-base max-w-2xl">
              Las cuotas son el lenguaje de las apuestas. Entender cómo funcionan, cómo compararlas y cómo detectar el margen de la casa es esencial para apostar con criterio.
            </p>
          </div>
        </div>

        {/* Formats */}
        <h3 className="text-white font-bold text-lg mb-5" style={{ fontFamily: "'Syne', sans-serif" }}>Formatos de cuotas</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-14">
          {oddsFormats.map((f) => (
            <div key={f.name} className="bg-[#0d0f14] border border-[#1e2028] rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-white font-semibold text-sm">{f.name}</h4>
                <span className="text-2xl font-bold" style={{ color: f.color, fontFamily: "'Syne', sans-serif" }}>{f.example}</span>
              </div>
              <p className="text-[#6b7280] text-xs leading-relaxed mb-4">{f.desc}</p>
              <div className="space-y-2">
                <div className="p-2.5 bg-[#080a0f] rounded-lg">
                  <p className="text-[#6b7280] text-xs mb-0.5">Fórmula</p>
                  <p className="text-[#a8b2c1] text-xs font-mono whitespace-pre-line">{f.formula}</p>
                </div>
                <div className="p-2.5 bg-[#080a0f] rounded-lg">
                  <p className="text-[#6b7280] text-xs mb-0.5">Ejemplo</p>
                  <p className="text-[#a8b2c1] text-xs font-mono whitespace-pre-line">{f.calc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Margin tips */}
        <h3 className="text-white font-bold text-lg mb-5" style={{ fontFamily: "'Syne', sans-serif" }}>Cómo sacar ventaja al mercado</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          {marginTips.map((t) => (
            <div key={t.title} className="flex gap-4 p-5 bg-[#0d0f14] border border-[#1e2028] rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#06b6d4]/10 flex-shrink-0">
                <i className={`${t.icon} text-[#06b6d4] text-base`}></i>
              </div>
              <div>
                <h4 className="text-white font-semibold text-sm mb-1">{t.title}</h4>
                <p className="text-[#6b7280] text-xs leading-relaxed">{t.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA comparador */}
        <div className="relative overflow-hidden rounded-2xl bg-[#0d0f14] border border-[#1e2028] p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#06b6d4]/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="relative">
            <h3 className="text-white font-bold text-xl mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>
              Compara cuotas en tiempo real
            </h3>
            <p className="text-[#a8b2c1] text-sm">
              Nuestro comparador analiza más de 20 casas de apuestas para encontrar siempre la mejor cuota disponible.
            </p>
          </div>
          <Link
            to="/comparador"
            className="relative flex-shrink-0 px-6 py-3 bg-[#06b6d4] text-black text-sm font-bold rounded-md hover:bg-[#0891b2] transition-colors whitespace-nowrap cursor-pointer"
          >
            Ir al Comparador
          </Link>
        </div>
      </div>
    </section>
  );
}
